import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function NotesPanel({ gameId, isGm }) {
  const [notes, setNotes] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [gmOnly, setGmOnly] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/games/${gameId}/notes`);
    setNotes(data);
  };

  useEffect(() => {
    load();
  }, [gameId]);

  const create = async () => {
    if (!title.trim()) return toast.error("Give the note a title");
    await api.post(`/games/${gameId}/notes`, { title, content, visibility: gmOnly ? "gm" : "shared" });
    setTitle("");
    setContent("");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/games/${gameId}/notes/${id}`);
    load();
  };

  return (
    <div className="flex flex-col h-full p-4 gap-4 overflow-y-auto scrollbar-thin" data-testid="notes-panel">
      <div className="bg-black/30 border border-white/10 rounded-xl p-4 space-y-3">
        <Input data-testid="note-title-input" placeholder="Note title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-black/30 border-white/10" />
        <Textarea data-testid="note-content-input" placeholder="Write your note…" value={content} onChange={(e) => setContent(e.target.value)} className="bg-black/30 border-white/10" rows={3} />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Switch data-testid="note-gm-only-switch" checked={gmOnly} onCheckedChange={setGmOnly} />
            <span className="text-xs text-[#f1e0c5]/60">{gmOnly ? "GM Private" : "Shared with table"}</span>
          </div>
          <Button data-testid="note-save-button" size="sm" onClick={create} className="rounded-full bg-[#D90429] hover:bg-[#b8031f]">
            Save Note
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {notes.map((n) => (
          <div key={n.id} data-testid={`note-${n.id}`} className="bg-black/20 border border-white/5 rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display text-lg leading-tight">{n.title}</p>
                <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/70">{n.visibility === "gm" ? "GM Private" : "Shared"} · {n.author_name}</p>
              </div>
              <button data-testid={`delete-note-${n.id}`} onClick={() => remove(n.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429] transition-colors duration-200">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-[#f1e0c5]/70 mt-2 whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
        {notes.length === 0 && <p className="text-sm text-[#f1e0c5]/30 text-center pt-6">No notes yet.</p>}
      </div>
    </div>
  );
}
